import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppBadgeWithCount } from '../../hooks/useAppBadge';
import Avatar from '../common/Avatar';
import PromoBanner from './PromoBanner';
import NewsBanner from './NewsBanner';
import HeaderBanner from './HeaderBanner';
import { toast } from 'sonner';
import { 
  Bell, 
  ChevronDown, 
  Phone, 
  Mail, 
  Calendar, 
  Users, 
  Settings,
  Plus,
  MessageSquare,
  CreditCard,
  FolderKanban,
  Clock,
  X,
  User,
  LogOut,
  ChevronRight,
  Megaphone,
  ExternalLink,
  Play,
  Pause,
  Timer,
  TrendingUp,
  Wallet,
  FileText,
  DollarSign,
  MapPin,
  Receipt,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CheckSquare,
  Download,
  Menu,
  Euro
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to build full avatar URL
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  if (avatarPath.startsWith('http')) return avatarPath;
  // If path starts with /api/, prepend backend URL
  if (avatarPath.startsWith('/api/')) {
    return `${BACKEND_URL}${avatarPath}`;
  }
  return avatarPath;
};

const Header = ({ collapsed, setMobileOpen }) => {
  const { user, logout, isAgency, isTeamMember, isClient } = useAuth();
  const { theme, getCurrentScheduleStatus, isCopiloteMode } = useTheme();
  const navigate = useNavigate();
  const [accountManager, setAccountManager] = useState(null);
  const [favoriteCreatives, setFavoriteCreatives] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // News ticker data - loaded from backend
  const [newsItems, setNewsItems] = useState([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Team member timer & earnings data
  const [currentTimer, setCurrentTimer] = useState(null);
  const [earnings, setEarnings] = useState({ today: 0, week: 0, month: 0, year: 0 });
  const [realtimeEarnings, setRealtimeEarnings] = useState(0);
  const [showTimerDetails, setShowTimerDetails] = useState(false);

  // Client wallet data
  const [walletBalance, setWalletBalance] = useState(null);
  
  // Agency info for header display
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  // Promo banner campaign
  const [promoCampaign, setPromoCampaign] = useState(null);

  // Active incidents from manifest
  const [activeIncidents, setActiveIncidents] = useState([]);
  
  // Flash announcements (bug resolutions, etc.)
  const [flashAnnouncements, setFlashAnnouncements] = useState([]);

  // Agency pending actions (tickets + validations)
  const [agencyPendingActions, setAgencyPendingActions] = useState(null);
  const [showTicketsDropdown, setShowTicketsDropdown] = useState(false);
  const [showValidationsDropdown, setShowValidationsDropdown] = useState(false);
  
  // Agency active timers
  const [activeTimers, setActiveTimers] = useState([]);
  const [showActiveTimers, setShowActiveTimers] = useState(false);

  // Agency margin data
  const [agencyMargin, setAgencyMargin] = useState({ today: 0, week: 0, month: 0, year: 0 });
  const [showMarginDetails, setShowMarginDetails] = useState(false);
  const [realtimeMargin, setRealtimeMargin] = useState(0);
  
  // Unread messages count for chat bubble
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [playMessageSound, setPlayMessageSound] = useState(false);
  const messageAudioRef = React.useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Client pending actions for notifications
  const [clientPendingActions, setClientPendingActions] = useState(null);

  useEffect(() => {
    // Fetch agency info for ALL users (needed for header logo)
    fetchAgencyInfo();
    fetchNotifications();
    fetchNews();
    fetchPromoCampaign();
    fetchActiveIncidents();
    fetchUnreadMessages(); // Fetch unread messages count
    fetchFlashAnnouncements(); // Fetch bug resolution announcements
    
    // Refresh incidents every 2 minutes
    const incidentInterval = setInterval(fetchActiveIncidents, 120000);
    
    // Poll unread messages every 15 seconds for all users
    const messageInterval = setInterval(fetchUnreadMessages, 15000);
    
    // Poll flash announcements every 60 seconds
    const flashInterval = setInterval(fetchFlashAnnouncements, 60000);
    
    if (isClient) {
      fetchAccountManager();
      fetchFavoriteCreatives();
      fetchWalletBalance();
      fetchClientPendingActions();
      // Refresh pending actions every 2 minutes
      const pendingInterval = setInterval(fetchClientPendingActions, 120000);
      return () => {
        clearInterval(pendingInterval);
        clearInterval(messageInterval);
        clearInterval(incidentInterval);
        clearInterval(flashInterval);
      };
    }
    if (isTeamMember) {
      fetchCurrentTimer();
      fetchEarnings();
      fetchAccountManager(); // Also fetch project manager for team members
      // Poll timer every 30 seconds
      const timerInterval = setInterval(fetchCurrentTimer, 30000);
      return () => {
        clearInterval(timerInterval);
        clearInterval(messageInterval);
        clearInterval(incidentInterval);
        clearInterval(flashInterval);
      };
    }
    if (isAgency) {
      fetchAgencyPendingActions();
      fetchActiveTimers();
      fetchAgencyMargin();
      // Refresh agency actions and timers every 30 seconds
      const agencyInterval = setInterval(() => {
        fetchAgencyPendingActions();
        fetchActiveTimers();
        fetchAgencyMargin();
      }, 30000);
      return () => {
        clearInterval(agencyInterval);
        clearInterval(messageInterval);
        clearInterval(incidentInterval);
        clearInterval(flashInterval);
      };
    }
    
    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Update schedule status every minute
    if (getCurrentScheduleStatus) {
      setScheduleStatus(getCurrentScheduleStatus());
      const scheduleInterval = setInterval(() => {
        setScheduleStatus(getCurrentScheduleStatus());
      }, 60000);
      return () => {
        clearInterval(timeInterval);
        clearInterval(scheduleInterval);
      };
    }
    
    return () => clearInterval(timeInterval);
  }, [isClient, isTeamMember, isAgency, theme.schedule]);

  // Listen for timer events from TimerContext
  useEffect(() => {
    const handleTimerStarted = () => {
      if (isTeamMember) {
        fetchCurrentTimer();
      }
      if (isAgency) {
        fetchActiveTimers();
      }
    };

    const handleTimerStopped = () => {
      if (isTeamMember) {
        fetchCurrentTimer();
      }
      if (isAgency) {
        fetchActiveTimers();
      }
    };

    window.addEventListener('timer-started', handleTimerStarted);
    window.addEventListener('timer-stopped', handleTimerStopped);

    return () => {
      window.removeEventListener('timer-started', handleTimerStarted);
      window.removeEventListener('timer-stopped', handleTimerStopped);
    };
  }, [isTeamMember, isAgency]);

  const fetchClientPendingActions = async () => {
    try {
      const res = await api.get('/dashboard/client/pending-actions');
      setClientPendingActions(res.data);
    } catch (error) {
      console.error('Error fetching pending actions:', error);
    }
  };

  const fetchAgencyPendingActions = async () => {
    try {
      const res = await api.get('/dashboard/agency/pending-actions');
      setAgencyPendingActions(res.data);
    } catch (error) {
      console.error('Error fetching agency pending actions:', error);
    }
  };

  const fetchActiveTimers = async () => {
    try {
      const res = await api.get('/time/all-active');
      setActiveTimers(res.data || []);
    } catch (error) {
      console.error('Error fetching active timers:', error);
    }
  };

  // Fetch agency margin data (day/week/month/year)
  const fetchAgencyMargin = async () => {
    try {
      const res = await api.get('/time/agency-margin');
      setAgencyMargin(res.data || { today: 0, week: 0, month: 0, year: 0 });
    } catch (error) {
      console.error('Error fetching agency margin:', error);
    }
  };

  // Calculate real-time margin for all active timers
  // Margin = (Budget task / Budget hours) - (Time spent × Member hourly rate)
  const calculateRealtimeMargin = () => {
    if (!activeTimers || activeTimers.length === 0) return 0;
    
    let totalMargin = 0;
    const now = new Date();
    
    activeTimers.forEach(timer => {
      if (!timer.start_time) return;
      const start = new Date(timer.start_time);
      if (isNaN(start.getTime())) return;
      const elapsedHours = Math.max(0, (now - start) / 3600000);
      
      // Client rate (what we charge) - default to 120€/h
      const clientRate = parseFloat(timer.client_hourly_rate || timer.project_hourly_rate) || 120;
      // Member rate (what we pay) - default to 30€/h
      const memberRate = parseFloat(timer.member_hourly_rate || timer.hourly_rate) || 30;
      
      // Margin per hour = Client rate - Member rate
      const marginPerHour = clientRate - memberRate;
      const timerMargin = elapsedHours * marginPerHour;
      
      if (!isNaN(timerMargin)) {
        totalMargin += timerMargin;
      }
    });
    
    return isNaN(totalMargin) ? 0 : totalMargin;
  };

  // Update real-time margin every second when there are active timers
  useEffect(() => {
    if (isAgency && activeTimers && activeTimers.length > 0) {
      const marginInterval = setInterval(() => {
        setRealtimeMargin(calculateRealtimeMargin());
      }, 1000);
      return () => clearInterval(marginInterval);
    } else {
      setRealtimeMargin(0);
    }
  }, [isAgency, activeTimers]);

  // Update real-time earnings for team members every second when there's an active timer
  useEffect(() => {
    if (isTeamMember && currentTimer && currentTimer.start_time) {
      const earningsInterval = setInterval(() => {
        const start = new Date(currentTimer.start_time);
        if (isNaN(start.getTime())) {
          setRealtimeEarnings(0);
          return;
        }
        const now = new Date();
        const diffHours = Math.max(0, (now - start) / 3600000);
        const hourlyRate = parseFloat(currentTimer.hourly_rate) || 50;
        const earnings = diffHours * hourlyRate;
        setRealtimeEarnings(isNaN(earnings) ? 0 : earnings);
      }, 1000);
      return () => clearInterval(earningsInterval);
    } else {
      setRealtimeEarnings(0);
    }
  }, [isTeamMember, currentTimer]);

  const stopTimer = async (timerId) => {
    try {
      await api.post('/time/stop', { entry_id: timerId });
      // Refresh timers based on user role
      if (isAgency) {
        fetchActiveTimers();
      }
      if (isTeamMember) {
        fetchCurrentTimer();
        fetchEarnings();
      }
      toast.success('Chronomètre arrêté');
    } catch (error) {
      console.error('Error stopping timer:', error);
      toast.error('Erreur lors de l\'arrêt du chronomètre');
    }
  };

  // Safe number formatting helper
  const safeNumber = (value, decimals = 0) => {
    const num = parseFloat(value);
    if (isNaN(num) || !isFinite(num)) return 0;
    return decimals > 0 ? num.toFixed(decimals) : Math.round(num);
  };

  const formatTimerDuration = (startTime) => {
    if (!startTime) return '0h00';
    const start = new Date(startTime);
    if (isNaN(start.getTime())) return '0h00';
    const now = new Date();
    const diffMs = Math.max(0, now - start);
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
  };

  // Calculate real-time earnings for a timer
  const calculateTimerEarnings = (startTime, hourlyRate) => {
    if (!startTime) return '0.00';
    const start = new Date(startTime);
    if (isNaN(start.getTime())) return '0.00';
    const now = new Date();
    const diffHours = Math.max(0, (now - start) / 3600000);
    const rate = parseFloat(hourlyRate) || 50;
    const earnings = diffHours * rate;
    return isNaN(earnings) ? '0.00' : earnings.toFixed(2);
  };

  // Calculate total earnings for all active timers
  const calculateTotalActiveEarnings = () => {
    if (!activeTimers || activeTimers.length === 0) return '0.00';
    return activeTimers.reduce((total, timer) => {
      const earnings = parseFloat(calculateTimerEarnings(timer.start_time, timer.hourly_rate)) || 0;
      return total + earnings;
    }, 0).toFixed(2);
  };

  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo(res.data);
    } catch (error) {
      // Use default values
      setAgencyInfo({
        name: "MSC L'AGENCE",
        address: "12 Rue de la Communication",
        postal_code: "75001",
        city: "Paris",
        phone: "+33 1 23 45 67 89"
      });
    }
  };

  // Fetch unread messages count
  const fetchUnreadMessages = async () => {
    try {
      const res = await api.get('/projects/chat/centralized/unread-total');
      const newCount = res.data?.total_unread || 0;
      
      // If count increased, play sound (for agency users)
      if (newCount > unreadMessages && unreadMessages > 0 && isAgency) {
        playNotificationSound();
      }
      
      setUnreadMessages(newCount);
    } catch (error) {
      // Silently fail - not critical
    }
  };

  // Play notification sound when new message arrives
  const playNotificationSound = () => {
    try {
      // Use default sound or custom sound from agency settings
      if (messageAudioRef.current) {
        messageAudioRef.current.currentTime = 0;
        messageAudioRef.current.play().catch(() => {});
      }
    } catch (error) {
      // Sound playback not critical
    }
  };

  const fetchPromoCampaign = async () => {
    try {
      const res = await api.get('/settings/promo-banner');
      if (res.data && res.data.enabled) {
        setPromoCampaign(res.data);
      }
    } catch (error) {
      // No active campaign
    }
  };

  const fetchActiveIncidents = async () => {
    try {
      const res = await api.get('/manifest/active-incidents');
      setActiveIncidents(res.data || []);
    } catch (error) {
      // No incidents or not authorized
    }
  };

  // Fetch flash announcements (bug resolutions, etc.)
  const fetchFlashAnnouncements = async () => {
    try {
      const res = await api.get('/bugs/flash-announcements');
      // Filter out dismissed announcements
      const filtered = (res.data || []).filter(ann => 
        !ann.dismissed_by?.includes(user?.id)
      );
      setFlashAnnouncements(filtered);
    } catch (error) {
      // No announcements
    }
  };

  const dismissFlashAnnouncement = async (announcementId) => {
    try {
      await api.post(`/bugs/flash-announcements/${announcementId}/dismiss`);
      setFlashAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    } catch (error) {
      // Ignore error
    }
  };

  const fetchCurrentTimer = async () => {
    try {
      const res = await api.get('/time/current');
      setCurrentTimer(res.data);
    } catch (error) {
      setCurrentTimer(null);
    }
  };

  const fetchEarnings = async () => {
    try {
      // Get earnings for different periods
      const [dayRes, weekRes, monthRes, yearRes] = await Promise.all([
        api.get('/time/report', { params: { period: 'day' } }).catch(() => ({ data: { total_earnings: 0 } })),
        api.get('/time/report', { params: { period: 'week' } }).catch(() => ({ data: { total_earnings: 0 } })),
        api.get('/time/report', { params: { period: 'month' } }).catch(() => ({ data: { total_earnings: 0 } })),
        api.get('/time/report', { params: { period: 'year' } }).catch(() => ({ data: { total_earnings: 0 } }))
      ]);
      
      setEarnings({
        today: dayRes.data?.total_earnings || 0,
        week: weekRes.data?.total_earnings || 0,
        month: monthRes.data?.total_earnings || 0,
        year: yearRes.data?.total_earnings || 0
      });
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const res = await api.get('/wallet/');
      setWalletBalance(res.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  // News ticker rotation
  useEffect(() => {
    if (newsItems.length > 0) {
      const newsInterval = setInterval(() => {
        setCurrentNewsIndex((prev) => (prev + 1) % newsItems.length);
      }, 5000);
      return () => clearInterval(newsInterval);
    }
  }, [newsItems.length]);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news/active');
      if (response.data?.length > 0) {
        setNewsItems(response.data.map(n => ({
          id: n.id,
          text: n.title,
          content: n.content,
          link: n.link,
          linkText: n.link_text || 'Découvrir',
          isPromo: n.is_promo,
          promoCode: n.promo_code,
          expiresAt: n.promo_expires_at,
          limitedSpots: n.limited_spots,
          spotsRemaining: n.spots_remaining
        })));
      } else {
        // Fallback to default news if none from backend
        setNewsItems([
          { id: 1, text: "Bienvenue sur votre Espace", link: "/dashboard", linkText: "Tableau de bord" }
        ]);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNewsItems([
        { id: 1, text: "Bienvenue sur MSC Manager", link: "/dashboard", linkText: "Tableau de bord" }
      ]);
    }
  };

  const fetchAccountManager = async () => {
    try {
      // Fetch the assigned manager for the current user (client or team member)
      const response = await api.get('/users/my-manager');
      if (response.data) {
        setAccountManager(response.data);
      }
    } catch (error) {
      console.error('Error fetching account manager:', error);
      // Fallback to agency user list
      try {
        const fallbackRes = await api.get('/users/', { params: { role: 'agency' } });
        if (fallbackRes.data?.length > 0) {
          setAccountManager(fallbackRes.data[0]);
        }
      } catch (e) {
        // Silent fail
      }
    }
  };

  const fetchFavoriteCreatives = async () => {
    setFavoriteCreatives([]);
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/');
      const notifs = (res.data || []).slice(0, 5).map(n => ({
        id: n.id,
        // Map both old and new field names
        type: n.notification_type || n.type,
        title: n.title,
        message: n.content || n.message,
        time: formatNotifTime(n.created_at),
        read: n.read || n.is_read,
        link: n.link,
        // Include IDs for navigation
        project_id: n.project_id,
        ticket_id: n.ticket_id,
        task_id: n.task_id,
        contract_id: n.contract_id
      }));
      setNotifications(notifs);
    } catch (error) {
      // Fallback to empty
      setNotifications([]);
    }
  };

  const formatNotifTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Calculate total badge count for app icon (notifications + tickets + validations + messages)
  const totalBadgeCount = (() => {
    let count = unreadCount; // Unread notifications
    
    // Agency: tickets + validations + documents
    if (isAgency && agencyPendingActions) {
      count += agencyPendingActions.pending_tickets || 0;
      count += agencyPendingActions.pending_validations || 0;
      count += agencyPendingActions.unread_documents || 0;
    }
    
    // Client: pending actions
    if (isClient && clientPendingActions) {
      count += clientPendingActions.unsigned_briefs || 0;
      count += clientPendingActions.pending_contracts || 0;
      count += clientPendingActions.pending_deliverables || 0;
      count += clientPendingActions.pending_payments || 0;
    }
    
    return count;
  })();

  // Update app badge on icon (PWA feature)
  useAppBadgeWithCount(totalBadgeCount);

  const getRoleBadge = () => {
    if (isAgency) return { label: 'Agence', color: 'bg-[var(--color-primary)] text-white' };
    if (isTeamMember) return { label: 'Équipier', color: 'bg-[#6366f1] text-white' };
    return { label: 'Client', color: 'bg-[var(--color-accent)] text-[var(--color-primary)]' };
  };

  const roleBadge = getRoleBadge();

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatShortDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short'
    });
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getDayName = (day) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[day] || day;
  };

  return (
    <>
      <header 
        className="fixed top-0 right-0 bg-white border-b border-[#e2e8f0] z-30 transition-all duration-300 overflow-hidden"
        style={{ 
          left: isMobile ? 0 : (collapsed ? 64 : 256)
        }}
      >
        {/* Header Banner - Announcements of type "header" from Admin > Announcements */}
        <HeaderBanner />
        
        {/* Active Incidents Banner */}
        {activeIncidents.length > 0 && (
          <div className="bg-gradient-to-r from-amber-500 to-red-500 text-white px-4 py-2">
            <div className="flex items-center justify-center gap-2 text-sm font-medium">
              <AlertTriangle className="w-4 h-4 animate-pulse" />
              <span>{activeIncidents[0].banner_message || activeIncidents[0].title}</span>
              {activeIncidents.length > 1 && (
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                  +{activeIncidents.length - 1} autre{activeIncidents.length > 2 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Flash Announcements Banner (Bug Resolutions) */}
        {flashAnnouncements.length > 0 && (
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2 flex-1 justify-center">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-medium">{flashAnnouncements[0].title}</span>
                <span className="opacity-80 hidden sm:inline">— {flashAnnouncements[0].content}</span>
              </div>
              <button 
                onClick={() => dismissFlashAnnouncement(flashAnnouncements[0].id)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* News Banner - For ALL authenticated users (replaces old news ticker) */}
        <NewsBanner />
        
        {/* Promo Banner - Only when there's an active promo campaign AND user is NOT client (clients use NewsBanner) */}
        {user?.role !== 'client' && promoCampaign && promoCampaign.enabled && (
          <PromoBanner campaign={promoCampaign} height="h-10" userRole={user?.role} />
        )}

        {/* Main Header - Responsive */}
        <div className="h-14 md:h-16 flex items-center justify-between px-3 md:px-6 relative z-20 bg-white">
          {/* Left side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Mobile Menu Button - Inside header */}
            {isMobile && setMobileOpen && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="md:hidden w-12 h-12 rounded-full bg-slate-100 hover:bg-slate-200"
              >
                <Menu className="h-7 w-7 text-slate-700" />
              </Button>
            )}
            
            {/* Date and Week Number - Hidden on mobile to save space */}
            <div className="hidden sm:flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-slate-100 rounded-full text-xs md:text-sm">
              <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-500" />
              <span className="text-slate-700 font-medium capitalize">
                {formatShortDate(currentTime)}
              </span>
              <span className="text-xs text-slate-500 border-l border-slate-300 pl-1.5 md:pl-2">
                S{getWeekNumber(currentTime)}
              </span>
            </div>
            
            {/* Schedule indicator with click for details - Visible for all users */}
            {theme.schedule?.enabled && scheduleStatus && (
              <button 
                onClick={() => setShowScheduleModal(true)}
                className={cn(
                  "flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium transition-all hover:scale-105",
                  scheduleStatus.isOpen 
                    ? "bg-green-100 text-green-700 hover:bg-green-200" 
                    : "bg-red-100 text-red-700 hover:bg-red-200"
                )}
                data-testid="schedule-indicator"
              >
                <div className={cn(
                  "w-2 h-2 rounded-full flex-shrink-0",
                  scheduleStatus.isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"
                )} />
                <span className="hidden xs:inline">{scheduleStatus.isOpen ? 'Ouvert' : 'Fermé'}</span>
                {/* Show current hours on larger screens */}
                <span className="hidden md:inline text-xs opacity-70">
                  {scheduleStatus.isOpen && scheduleStatus.closesAt 
                    ? `jusqu'à ${scheduleStatus.closesAt}` 
                    : scheduleStatus.opensAt 
                      ? `ouvre à ${scheduleStatus.opensAt}`
                      : formatTime(currentTime)
                  }
                </span>
                <span className="md:hidden text-xs opacity-70">
                  {formatTime(currentTime)}
                </span>
              </button>
            )}

            {/* TEAM MEMBER: Quick Actions (sans le timer ici - déplacé à droite) */}
            {isTeamMember && (
              <div className="flex items-center gap-2">
                {/* Quick link to tasks */}
                <Button 
                  onClick={() => navigate('/my-tasks')}
                  size="sm"
                  variant="outline"
                  className="hidden sm:flex rounded-full"
                >
                  <FolderKanban className="w-4 h-4 mr-1" />
                  Mes tâches
                </Button>
              </div>
            )}

            {/* CLIENT: Credit Time Badge + Quick Actions */}
            {isClient && (
              <div className="flex items-center gap-2">
                {/* Credit Time Badge */}
                {walletBalance && (
                  <button
                    onClick={() => navigate('/wallet')}
                    className={cn(
                      "credit-badge",
                      (walletBalance.available_hours ?? walletBalance.balance_hours) < 5 && (walletBalance.available_hours ?? walletBalance.balance_hours) > 0 && "credit-badge-low",
                      (walletBalance.available_hours ?? walletBalance.balance_hours) <= 0 && "credit-badge-critical"
                    )}
                    title={`${(walletBalance.balance_amount || 0).toLocaleString('fr-FR')}€ HT disponible`}
                  >
                    <Wallet className={cn(
                      "w-4 h-4",
                      (walletBalance.available_hours ?? walletBalance.balance_hours) <= 0 ? "text-red-600" :
                      (walletBalance.available_hours ?? walletBalance.balance_hours) < 5 ? "text-amber-600" : "text-indigo-600"
                    )} />
                    <span className={cn(
                      "font-mono font-semibold",
                      (walletBalance.available_hours ?? walletBalance.balance_hours) <= 0 ? "text-red-700" :
                      (walletBalance.available_hours ?? walletBalance.balance_hours) < 5 ? "text-amber-700" : "text-indigo-700"
                    )}>
                      {safeNumber(walletBalance.available_hours ?? walletBalance.balance_hours, 1) || 0}h
                    </span>
                    <span className={cn(
                      "text-xs hidden sm:inline",
                      (walletBalance.available_hours ?? walletBalance.balance_hours) <= 0 ? "text-red-600" :
                      (walletBalance.available_hours ?? walletBalance.balance_hours) < 5 ? "text-amber-600" : "text-indigo-600"
                    )}>
                      ({(walletBalance.balance_amount || 0).toLocaleString('fr-FR')}€ HT)
                    </span>
                  </button>
                )}

                <Button 
                  onClick={() => navigate('/new-project-request')}
                  size="sm"
                  className="hidden sm:flex rounded-full"
                  style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nouvelle demande
                </Button>
                <Button 
                  onClick={() => navigate('/tickets/new')}
                  size="sm"
                  variant="outline"
                  className="hidden md:flex rounded-full"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Ticket
                </Button>
                <Button 
                  onClick={() => navigate('/wallet')}
                  size="sm"
                  variant="outline"
                  className="hidden lg:flex rounded-full"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Recharger
                </Button>
              </div>
            )}

            {/* Quick Actions for Agency */}
            {isAgency && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm"
                    className="rounded-full text-white"
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Action rapide</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/my-tasks')} className="cursor-pointer">
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Mes tâches
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/prospects?new=true')} className="cursor-pointer">
                    <Users className="w-4 h-4 mr-2" />
                    Nouveau prospect
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/projects?new=true')} className="cursor-pointer">
                    <FolderKanban className="w-4 h-4 mr-2" />
                    Nouveau projet
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/tickets?new=true')} className="cursor-pointer">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Nouveau ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/client-news?new=true')} className="cursor-pointer">
                    <Megaphone className="w-4 h-4 mr-2" />
                    Nouvelle annonce
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Agency Tickets Icon */}
            {isAgency && agencyPendingActions && (
              <DropdownMenu open={showTicketsDropdown} onOpenChange={setShowTicketsDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative rounded-full h-9 w-9 sm:h-10 sm:w-10">
                    <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                    {agencyPendingActions.open_tickets > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-medium">
                        {agencyPendingActions.open_tickets}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 sm:w-80">
                  <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b bg-blue-50">
                    <h3 className="font-semibold text-blue-700 flex items-center gap-2 text-sm">
                      <MessageSquare className="w-4 h-4" />
                      Tickets ouverts
                    </h3>
                    <Badge className="bg-blue-100 text-blue-700">
                      {agencyPendingActions.open_tickets}
                    </Badge>
                  </div>
                  <div className="max-h-60 sm:max-h-80 overflow-y-auto">
                    {agencyPendingActions.tickets_list?.length > 0 ? (
                      agencyPendingActions.tickets_list.map((ticket) => (
                        <div 
                          key={ticket.id}
                          className="p-2 sm:p-3 border-b hover:bg-slate-50 cursor-pointer"
                          onClick={() => {
                            navigate(`/tickets/${ticket.id}`);
                            setShowTicketsDropdown(false);
                          }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-xs sm:text-sm truncate" style={{ color: theme.colors?.primary }}>
                              {ticket.subject}
                            </p>
                            <Badge variant="outline" className={cn(
                              "text-[10px] sm:text-xs ml-2",
                              ticket.priority === 'high' && "border-red-300 text-red-600",
                              ticket.priority === 'normal' && "border-yellow-300 text-yellow-600"
                            )}>
                              {ticket.priority === 'high' ? 'Urgent' : 'Normal'}
                            </Badge>
                          </div>
                          <p className="text-[10px] sm:text-xs text-[#64748b] truncate">{ticket.messages?.[0]?.message || 'Nouveau ticket'}</p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-[#64748b]">
                        <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs sm:text-sm">Aucun ticket ouvert</p>
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t">
                    <Button 
                      variant="outline" 
                      className="w-full text-xs sm:text-sm rounded-lg gap-2"
                      onClick={() => {
                        navigate('/tickets');
                        setShowTicketsDropdown(false);
                      }}
                    >
                      Voir tous les tickets
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Agency Task Validations Icon - Dedicated for task validations */}
            {isAgency && agencyPendingActions && (agencyPendingActions.tasks_awaiting_agency > 0 || agencyPendingActions.tasks_awaiting_client > 0) && (
              <DropdownMenu open={showValidationsDropdown} onOpenChange={setShowValidationsDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative rounded-full h-9 w-9 sm:h-10 sm:w-10 bg-gradient-to-br from-cyan-50 to-green-50 hover:from-cyan-100 hover:to-green-100">
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-cyan-500 to-green-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-bold shadow-lg animate-pulse">
                      {(agencyPendingActions.tasks_awaiting_agency || 0) + (agencyPendingActions.tasks_awaiting_client || 0)}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 sm:w-96">
                  <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-cyan-50 to-green-50">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                      Tâches à valider
                    </h3>
                    <Badge className="bg-white text-slate-700 border shadow-sm">
                      {(agencyPendingActions.tasks_awaiting_agency || 0) + (agencyPendingActions.tasks_awaiting_client || 0)} tâche(s)
                    </Badge>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {/* Tasks awaiting AGENCY validation (submitted status) */}
                    {agencyPendingActions.tasks_awaiting_agency > 0 && (
                      <div className="p-3 border-b">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-cyan-700">
                              Validation Agence
                            </span>
                            <p className="text-xs text-slate-500">Soumises par l'équipe</p>
                          </div>
                          <Badge className="ml-auto bg-cyan-100 text-cyan-700">
                            {agencyPendingActions.tasks_awaiting_agency}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {agencyPendingActions.tasks_awaiting_agency_list?.slice(0, 5).map((task) => (
                            <div 
                              key={task.id}
                              className="p-3 rounded-lg bg-cyan-50 hover:bg-cyan-100 cursor-pointer transition-colors border border-cyan-100 hover:border-cyan-200"
                              onClick={() => {
                                navigate(`/tasks/${task.id}`);
                                setShowValidationsDropdown(false);
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-slate-800 truncate">{task.name || task.title}</p>
                                  <p className="text-xs text-cyan-600 truncate">{task.project_name || 'Projet'}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px] bg-white border-cyan-200 text-cyan-700">
                                  À valider
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Tasks awaiting CLIENT validation (agency_validated status) */}
                    {agencyPendingActions.tasks_awaiting_client > 0 && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-green-700">
                              Validation Client
                            </span>
                            <p className="text-xs text-slate-500">En attente du client</p>
                          </div>
                          <Badge className="ml-auto bg-green-100 text-green-700">
                            {agencyPendingActions.tasks_awaiting_client}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {agencyPendingActions.tasks_awaiting_client_list?.slice(0, 5).map((task) => (
                            <div 
                              key={task.id}
                              className="p-3 rounded-lg bg-green-50 hover:bg-green-100 cursor-pointer transition-colors border border-green-100 hover:border-green-200"
                              onClick={() => {
                                navigate(`/tasks/${task.id}`);
                                setShowValidationsDropdown(false);
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-slate-800 truncate">{task.name || task.title}</p>
                                  <p className="text-xs text-green-600 truncate">{task.project_name || 'Projet'}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px] bg-white border-green-200 text-green-700">
                                  Validé agence ✓
                                </Badge>
                                <span className="text-[10px] text-slate-500">→ En attente client</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t bg-slate-50">
                    <Button 
                      variant="outline" 
                      className="w-full text-xs sm:text-sm rounded-lg gap-2"
                      onClick={() => {
                        navigate('/projects');
                        setShowValidationsDropdown(false);
                      }}
                    >
                      Voir tous les projets
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Agency Other Validations Icon - Briefs and wallet top-ups */}
            {isAgency && agencyPendingActions && (agencyPendingActions.briefs_pending > 0 || agencyPendingActions.pending_wallet_topups > 0) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative rounded-full h-9 w-9 sm:h-10 sm:w-10">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-sky-500" />
                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-sky-400 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-medium shadow-sm">
                      {(agencyPendingActions.briefs_pending || 0) + (agencyPendingActions.pending_wallet_topups || 0)}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 sm:w-80">
                  <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b bg-sky-50">
                    <h3 className="font-semibold text-sky-700 flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4" />
                      Autres validations
                    </h3>
                  </div>
                  <div className="max-h-60 sm:max-h-80 overflow-y-auto">
                    {/* Briefs to sign */}
                    {agencyPendingActions.briefs_pending > 0 && (
                      <div className="p-2 sm:p-3 border-b">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <span className="text-xs sm:text-sm font-medium" style={{ color: theme.colors?.primary }}>
                            Briefs à signer ({agencyPendingActions.briefs_pending})
                          </span>
                        </div>
                        {agencyPendingActions.briefs_list?.slice(0, 3).map((brief) => (
                          <div 
                            key={brief.id}
                            className="text-[10px] sm:text-xs p-2 rounded bg-blue-50 hover:bg-blue-100 cursor-pointer mb-1"
                            onClick={() => {
                              navigate(`/projects/${brief.project_id}`);
                            }}
                          >
                            <p className="font-medium truncate">{brief.project_name || 'Projet'}</p>
                            <p className="text-blue-600">
                              {!brief.agency_signed ? 'Signature agence requise' : 'En attente client'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Pending wallet top-ups */}
                    {agencyPendingActions.pending_wallet_topups > 0 && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Wallet className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                            Virements à valider ({agencyPendingActions.pending_wallet_topups})
                          </span>
                        </div>
                        {agencyPendingActions.topups_list?.slice(0, 3).map((topup) => (
                          <div 
                            key={topup.id}
                            className="text-xs p-2 rounded bg-green-50 hover:bg-green-100 cursor-pointer mb-1"
                            onClick={() => {
                              navigate('/wallet-management');
                            }}
                          >
                            <p className="font-medium truncate">{topup.client_name}</p>
                            <p className="text-green-600">{topup.amount}€ - Virement bancaire</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Agency Margin Widget */}
            {isAgency && (
              <DropdownMenu open={showMarginDetails} onOpenChange={setShowMarginDetails}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={cn(
                      "relative rounded-full h-9 sm:h-10 px-3 gap-2",
                      activeTimers.length > 0 
                        ? "bg-gradient-to-br from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100"
                        : "bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100"
                    )}
                  >
                    <Euro className={cn(
                      "h-4 w-4 sm:h-5 sm:w-5",
                      activeTimers.length > 0 ? "text-emerald-600" : "text-blue-600"
                    )} />
                    <span className={cn(
                      "hidden sm:inline font-bold text-sm",
                      activeTimers.length > 0 ? "text-emerald-700" : "text-blue-700"
                    )}>
                      {activeTimers.length > 0 
                        ? `+${safeNumber((agencyMargin.today || 0) + realtimeMargin)}€`
                        : `${safeNumber(agencyMargin.today)}€`
                      }
                    </span>
                    {activeTimers.length > 0 && (
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <div className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-green-50">
                    <h3 className="font-semibold text-emerald-700 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Marge générée
                    </h3>
                    {activeTimers.length > 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        +{safeNumber(realtimeMargin, 2)}€ en cours
                      </p>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Aujourd'hui</span>
                      <span className="font-bold text-lg text-emerald-600">
                        {safeNumber((agencyMargin.today || 0) + realtimeMargin)}€
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Cette semaine</span>
                      <span className="font-semibold text-slate-800">
                        {safeNumber((agencyMargin.week || 0) + realtimeMargin)}€
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Ce mois</span>
                      <span className="font-semibold text-slate-800">
                        {safeNumber((agencyMargin.month || 0) + realtimeMargin)}€
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-sm font-medium text-slate-700">Cette année</span>
                      <span className="font-bold text-xl" style={{ color: theme.colors?.accent }}>
                        {safeNumber((agencyMargin.year || 0) + realtimeMargin)}€
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-2 border-t bg-slate-50">
                    <p className="text-xs text-slate-500 text-center">
                      Marge = Prix client - Coût membre
                    </p>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Agency Active Timers */}
            {isAgency && activeTimers.length > 0 && (
              <DropdownMenu open={showActiveTimers} onOpenChange={setShowActiveTimers}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative rounded-full h-9 sm:h-10 px-3 bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 gap-2">
                    <Timer className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                    <span className="hidden sm:inline text-green-700 font-semibold text-sm">
                      {calculateTotalActiveEarnings()}€
                    </span>
                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
                      {activeTimers.length}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 sm:w-96">
                  <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                    <h3 className="font-semibold text-green-700 flex items-center gap-2 text-sm">
                      <Timer className="w-4 h-4" />
                      Chronos actifs
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700">
                        {activeTimers.length} en cours
                      </Badge>
                      <Badge className="bg-emerald-500 text-white font-bold">
                        {calculateTotalActiveEarnings()}€
                      </Badge>
                    </div>
                  </div>
                  <div className="max-h-60 sm:max-h-80 overflow-y-auto">
                    {activeTimers.map((timer) => (
                      <div key={timer.id} className="p-3 border-b last:border-b-0 hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                            <span className="font-medium text-sm">{timer.user_name || 'Membre'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold text-green-600">
                              {formatTimerDuration(timer.start_time)}
                            </span>
                            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                              {calculateTimerEarnings(timer.start_time, timer.hourly_rate)}€
                            </span>
                          </div>
                        </div>
                        {timer.task_name && (
                          <p className="text-xs text-slate-600 mt-1 truncate">🎯 {timer.task_name}</p>
                        )}
                        {timer.project_name && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">📁 {timer.project_name}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">
                            {timer.hourly_rate || 50}€/h
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => stopTimer(timer.id)}
                          >
                            <Pause className="w-3 h-3 mr-1" />
                            Arrêter
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Favorite creatives for clients */}
            {isClient && favoriteCreatives.length > 0 && (
              <div className="hidden 2xl:flex items-center gap-2 px-3 py-1.5 bg-[#f8fafc] rounded-full">
                <Users className="h-4 w-4 text-[#64748b]" />
                <span className="text-xs text-[#64748b]">Favoris:</span>
                <div className="flex -space-x-2">
                  {favoriteCreatives.slice(0, 3).map((creative) => (
                    <Avatar 
                      key={creative.id}
                      name={creative.name}
                      size="xs"
                      type="creative"
                      className="border-2 border-white"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right side - User info */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* TEAM MEMBER: Timer Widget + Earnings - Positionné à droite - Masqué sur mobile */}
            {isTeamMember && (
              <div className="hidden sm:flex items-center gap-2">
                {/* Current Timer - Real-time display with stop button */}
                {currentTimer ? (
                  <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <button
                      onClick={() => setShowTimerDetails(true)}
                      className="flex items-center gap-2 hover:bg-emerald-100 rounded-full px-2 py-0.5 transition-colors"
                    >
                      <Timer className="w-4 h-4 text-emerald-600" />
                      <span className="font-mono font-semibold text-emerald-700">
                        {formatTimerDuration(currentTimer.start_time)}
                      </span>
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded hidden sm:inline">
                        +{calculateTimerEarnings(currentTimer.start_time, currentTimer.hourly_rate)}€
                      </span>
                    </button>
                    <button
                      onClick={() => stopTimer(currentTimer.id)}
                      className="p-1.5 rounded-full text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Arrêter le chrono"
                    >
                      <Pause className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/my-tasks')}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors"
                  >
                    <Play className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-600 hidden sm:inline">Démarrer chrono</span>
                  </button>
                )}

                {/* Quick Earnings Summary */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 hover:border-emerald-300 transition-all">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span className="font-mono font-semibold text-emerald-700">
                        {(earnings.today + realtimeEarnings).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€
                      </span>
                      <span className="text-xs text-emerald-600">aujourd'hui</span>
                      <ChevronDown className="w-3 h-3 text-emerald-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="p-3 border-b">
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>
                        Mes gains
                      </h4>
                      {realtimeEarnings > 0 && (
                        <p className="text-xs text-emerald-600 mt-1">
                          +{safeNumber(realtimeEarnings, 2)}€ en cours
                        </p>
                      )}
                    </div>
                    <div className="p-2 space-y-2">
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-emerald-50">
                        <span className="text-sm text-gray-600">Aujourd'hui</span>
                        <span className="font-mono font-bold text-emerald-700">
                          {(earnings.today + realtimeEarnings).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-cyan-50">
                        <span className="text-sm text-gray-600">Cette semaine</span>
                        <span className="font-mono font-bold text-cyan-700">
                          {(earnings.week + realtimeEarnings).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-violet-50">
                        <span className="text-sm text-gray-600">Ce mois</span>
                        <span className="font-mono font-bold text-violet-700">
                          {(earnings.month + realtimeEarnings).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-amber-50">
                        <span className="text-sm text-gray-600">Cette année</span>
                        <span className="font-mono font-bold text-amber-700">
                          {(earnings.year + realtimeEarnings).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                        </span>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Account Manager with Calendar link - visible for clients */}
            {isClient && accountManager && (
              <div className="hidden md:flex items-center gap-2 px-2 py-1 bg-[#f8fafc] rounded-full border border-[#e2e8f0]">
                {accountManager.avatar_url ? (
                  <img 
                    src={getAvatarUrl(accountManager.avatar_url)}
                    alt={`${accountManager.first_name} ${accountManager.last_name}`}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <Avatar 
                    name={`${accountManager.first_name} ${accountManager.last_name}`}
                    size="xs"
                    type="agency"
                  />
                )}
                <div className="hidden xl:block">
                  <p className="text-[10px] text-[#64748b] leading-none">Responsable</p>
                  <p className="text-xs font-medium text-[var(--color-primary)] leading-tight">
                    {accountManager.first_name}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {accountManager.phone && (
                    <a 
                      href={`tel:${accountManager.phone}`} 
                      className="flex items-center gap-1 text-xs text-[var(--color-accent)] hover:opacity-70 transition-opacity"
                      title="Appeler"
                    >
                      <Phone className="h-3 w-3" />
                      <span className="hidden lg:inline">{accountManager.phone}</span>
                    </a>
                  )}
                  {accountManager.email && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full hover:bg-slate-200" asChild>
                      <a href={`mailto:${accountManager.email}`} title="Email">
                        <Mail className="h-3 w-3 text-[var(--color-accent)]" />
                      </a>
                    </Button>
                  )}
                  <button 
                    className="h-6 w-6 rounded-full hover:bg-slate-200 flex items-center justify-center cursor-pointer"
                    title="Mes RDV"
                    type="button"
                    onClick={() => {
                      const scrollToMesRDV = () => {
                        const section = document.getElementById('mes-rdv-section');
                        if (section) {
                          const rect = section.getBoundingClientRect();
                          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                          window.scrollTo({
                            top: rect.top + scrollTop - 80,
                            behavior: 'smooth'
                          });
                        }
                      };
                      
                      if (window.location.pathname.includes('/dashboard')) {
                        scrollToMesRDV();
                      } else {
                        navigate('/dashboard');
                        setTimeout(scrollToMesRDV, 800);
                      }
                    }}
                  >
                    <Calendar className="h-3 w-3 text-[var(--color-accent)]" />
                  </button>
                </div>
              </div>
            )}

            {/* Project Manager with Calendar link - visible for team members */}
            {isTeamMember && accountManager && (
              <div className="hidden md:flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-full border border-violet-200">
                <Avatar 
                  name={`${accountManager.first_name} ${accountManager.last_name}`}
                  size="xs"
                  type="agency"
                />
                <div className="hidden xl:block">
                  <p className="text-[10px] text-violet-500 leading-none">Chef de projet</p>
                  <p className="text-xs font-medium text-violet-700 leading-tight">
                    {accountManager.first_name}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {accountManager.phone && (
                    <a 
                      href={`tel:${accountManager.phone}`} 
                      className="flex items-center gap-1 text-xs text-violet-500 hover:underline"
                      title="Appeler"
                    >
                      <Phone className="h-3 w-3" />
                      <span className="hidden lg:inline">{accountManager.phone}</span>
                    </a>
                  )}
                  {accountManager.email && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" asChild>
                      <a href={`mailto:${accountManager.email}`} title="Email">
                        <Mail className="h-3 w-3 text-violet-500" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Client Pending Actions Alert - Special icon */}
            {isClient && clientPendingActions && clientPendingActions.total_pending > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative rounded-full bg-green-50 hover:bg-green-100">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse shadow-lg">
                      {clientPendingActions.total_pending}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 sm:w-96">
                  <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                    <h3 className="font-semibold text-green-700 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Tâches à valider
                    </h3>
                    <Badge className="bg-white text-green-700 border border-green-200">
                      {clientPendingActions.total_pending}
                    </Badge>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {/* Tasks awaiting validation */}
                    {clientPendingActions.tasks_awaiting_validation > 0 && (
                      <div className="p-3 border-b">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-green-700">
                              Tâches en attente
                            </span>
                            <p className="text-xs text-slate-500">Validez pour poursuivre</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {clientPendingActions.tasks_list?.slice(0, 5).map((task) => (
                            <div 
                              key={task.id}
                              className="p-3 rounded-lg bg-green-50 hover:bg-green-100 cursor-pointer transition-colors border border-green-100 hover:border-green-200"
                              onClick={() => navigate(`/tasks/${task.id}`)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-slate-800 truncate">{task.name}</p>
                                  <p className="text-xs text-green-600 truncate">{task.project_name}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                              </div>
                              <div className="mt-2">
                                <Badge variant="outline" className="text-[10px] bg-white border-green-200 text-green-700">
                                  ✓ Validé agence — À vous de valider
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Briefs to sign */}
                    {clientPendingActions.briefs_to_sign > 0 && (
                      <div className="p-3 border-b">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                            Briefs à signer ({clientPendingActions.briefs_to_sign})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {clientPendingActions.briefs_list?.slice(0, 3).map((brief) => (
                            <div 
                              key={brief.id}
                              className="text-xs p-2 rounded bg-blue-50 hover:bg-blue-100 cursor-pointer"
                              onClick={() => navigate(`/projects/${brief.project_id}`)}
                            >
                              <p className="font-medium truncate">{brief.project_name}</p>
                              <p className="text-blue-600">Brief en attente de votre signature</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Projects to validate */}
                    {clientPendingActions.projects_to_validate > 0 && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <FolderKanban className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                            Livrables à valider ({clientPendingActions.projects_to_validate})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {clientPendingActions.projects_list?.slice(0, 3).map((project) => (
                            <div 
                              key={project.id}
                              className="text-xs p-2 rounded bg-purple-50 hover:bg-purple-100 cursor-pointer"
                              onClick={() => navigate(`/projects/${project.id}`)}
                            >
                              <p className="font-medium truncate">{project.name}</p>
                              <p className="text-purple-600">
                                {project.status === 'delivered' ? 'Livré - En attente de validation' : 'En révision'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t">
                    <Button 
                      variant="outline" 
                      className="w-full text-sm rounded-lg gap-2"
                      onClick={() => navigate('/my-projects')}
                    >
                      Voir tous mes projets
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Chat Messages Button with unread count */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative rounded-full" 
              data-testid="header-chat-btn"
              onClick={() => navigate('/chat')}
            >
              <MessageSquare className="h-5 w-5 text-[#64748b]" />
              {unreadMessages > 0 && (
                <span 
                  className={cn(
                    "absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-[#6366f1] text-white text-xs rounded-full flex items-center justify-center font-medium",
                    "animate-pulse"
                  )}
                >
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </Button>

            {/* Notifications dropdown */}
            <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-full" data-tour="header-notifications">
                  <Bell className="h-5 w-5 text-[#64748b]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-alert)] text-white text-xs rounded-full flex items-center justify-center font-medium">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="font-semibold" style={{ color: theme.colors?.primary }}>
                    Notifications
                  </h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs hover:underline"
                      style={{ color: theme.colors?.accent }}
                    >
                      Tout marquer comme lu
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-[#64748b]">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucune notification</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id}
                        data-testid={`notification-item-${notif.id}`}
                        onClick={(e) => {
                          // Prevent event propagation to avoid dropdown interference
                          e.stopPropagation();
                          
                          // Determine redirect path FIRST
                          const notifType = notif.type;
                          let targetPath = null;
                          
                          // Priority 1: Direct link provided (with route corrections)
                          if (notif.link) {
                            targetPath = notif.link;
                            // Fix broken routes
                            if (targetPath === '/receipts') {
                              targetPath = '/receipts/pending';
                            }
                          }
                          // Priority 2: Entity-specific redirects
                          else if (notif.project_id) {
                            targetPath = `/projects/${notif.project_id}`;
                          } else if (notif.ticket_id) {
                            targetPath = `/tickets/${notif.ticket_id}`;
                          } else if (notif.contract_id) {
                            targetPath = `/contracts/${notif.contract_id}`;
                          } else if (notif.task_id) {
                            targetPath = `/tasks/${notif.task_id}`;
                          }
                          // Priority 3: Type-based redirects
                          else if (notifType === 'payment' || notifType === 'wallet') {
                            targetPath = '/wallet';
                          } else if (notifType === 'action') {
                            targetPath = '/notifications';
                          }
                          
                          // Mark notification as read (background, don't wait)
                          api.put(`/notifications/${notif.id}/read`).then(() => {
                            setNotifications(prev => prev.filter(n => n.id !== notif.id));
                          }).catch(err => console.error('Error marking notification as read:', err));
                          
                          // Navigate FIRST, then close dropdown
                          if (targetPath) {
                            navigate(targetPath);
                          }
                          
                          // Close dropdown after navigation is initiated
                          setShowNotifications(false);
                        }}
                        className={cn(
                          "px-4 py-3 border-b border-[#f1f5f9] hover:bg-[#f8fafc] cursor-pointer transition-colors",
                          !notif.read && "bg-[#f0fdf4]"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${theme.colors?.accent}20` }}
                          >
                            {notif.type === 'project' && <FolderKanban className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'payment' && <CreditCard className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'brief' && <FileText className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'ticket' && <MessageSquare className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'task' && <Clock className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'wallet' && <Wallet className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {!['project', 'payment', 'brief', 'ticket', 'task', 'wallet'].includes(notif.type) && 
                              <Bell className="w-4 h-4" style={{ color: theme.colors?.accent }} />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                              {notif.title}
                            </p>
                            <p className="text-xs text-[#64748b] truncate">{notif.message}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-[#94a3b8] flex-shrink-0">{notif.time}</span>
                            <ChevronRight className="w-3 h-3 text-[#94a3b8]" />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm rounded-lg"
                    onClick={() => navigate('/notifications')}
                  >
                    Voir toutes les notifications
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User dropdown - Simplified */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-[#f8fafc] transition-colors" data-tour="user-menu">
                  <Avatar 
                    src={getAvatarUrl(user?.avatar_url)}
                    name={`${user?.first_name} ${user?.last_name}`}
                    size="md"
                    type={isAgency ? 'agency' : isTeamMember ? 'creative' : 'client'}
                  />
                  <ChevronDown className="h-4 w-4 text-[#64748b] hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b border-[#e2e8f0]">
                  <p className="font-medium text-[var(--color-primary)]">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-[#64748b]">{user?.email || user?.username}</p>
                  <Badge className={cn("text-xs mt-1 rounded-full", roleBadge.color)}>
                    {roleBadge.label}
                  </Badge>
                </div>
                
                {/* Mobile quick actions for clients */}
                {isClient && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/new-project-request')} className="sm:hidden">
                      <Plus className="h-4 w-4 mr-2" />
                      Nouvelle demande
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/tickets/new')} className="md:hidden">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Ouvrir un ticket
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/wallet')} className="lg:hidden">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Recharger wallet
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="lg:hidden" />
                  </>
                )}
                
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="h-4 w-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  try {
                    await api.put('/users/preferences', {
                      interactive_tour_completed: false,
                      interactive_tour_skipped: false,
                      guided_tour_completed: false,
                      guided_tour_skipped: false
                    });
                    window.location.reload();
                  } catch (e) {
                    console.error('Error resetting tour:', e);
                  }
                }}>
                  <MapPin className="h-4 w-4 mr-2" />
                  Visite guidée
                </DropdownMenuItem>
                {isAgency && (
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Personnalisation
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md" aria-describedby="schedule-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Clock className="w-5 h-5" />
              Horaires de l'agence
            </DialogTitle>
          </DialogHeader>
          <p id="schedule-description" className="sr-only">Horaires d'ouverture de l'agence MSC</p>
          
          <div className="space-y-4">
            {/* Current status */}
            <div 
              className={cn(
                "p-4 rounded-xl",
                scheduleStatus?.isOpen ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-3 h-3 rounded-full",
                  scheduleStatus?.isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"
                )} />
                <div>
                  <p className={cn(
                    "font-semibold",
                    scheduleStatus?.isOpen ? "text-green-700" : "text-red-700"
                  )}>
                    {scheduleStatus?.isOpen ? 'Actuellement ouvert' : 'Actuellement fermé'}
                  </p>
                  <p className="text-sm text-[#64748b]">{scheduleStatus?.message}</p>
                </div>
              </div>
            </div>

            {/* Date and time */}
            <div className="p-4 bg-[#f8fafc] rounded-xl">
              <p className="text-sm text-[#64748b]">Date et heure actuelles</p>
              <p className="text-lg font-semibold capitalize" style={{ color: theme.colors?.primary }}>
                {formatDate(currentTime)}
              </p>
              <p className="text-2xl font-mono font-bold" style={{ color: theme.colors?.accent }}>
                {formatTime(currentTime)}
              </p>
            </div>

            {/* Weekly schedule - Grouped format */}
            <div>
              <h4 className="font-medium mb-3" style={{ color: theme.colors?.primary }}>
                Horaires de la semaine
              </h4>
              <div className="space-y-2">
                {(() => {
                  // Group days with same hours
                  const schedule = theme.schedule?.regular || {};
                  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                  const dayNamesShort = {
                    monday: 'Lun', tuesday: 'Mar', wednesday: 'Mer', 
                    thursday: 'Jeu', friday: 'Ven', saturday: 'Sam', sunday: 'Dim'
                  };
                  const dayNamesFull = {
                    monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi', 
                    thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
                  };
                  
                  // Create grouped display
                  const groups = [];
                  let currentGroup = null;
                  
                  dayOrder.forEach((day, index) => {
                    const daySchedule = schedule[day] || { closed: true };
                    const key = daySchedule.closed ? 'closed' : `${daySchedule.open}-${daySchedule.close}`;
                    
                    if (!currentGroup || currentGroup.key !== key) {
                      if (currentGroup) groups.push(currentGroup);
                      currentGroup = {
                        key,
                        days: [day],
                        schedule: daySchedule
                      };
                    } else {
                      currentGroup.days.push(day);
                    }
                  });
                  if (currentGroup) groups.push(currentGroup);
                  
                  // Format the display
                  const todayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][new Date().getDay()];
                  
                  return groups.map((group, idx) => {
                    const isToday = group.days.includes(todayName);
                    let dayLabel;
                    if (group.days.length === 1) {
                      dayLabel = dayNamesFull[group.days[0]];
                    } else if (group.days.length === 5 && group.days[0] === 'monday' && group.days[4] === 'friday') {
                      dayLabel = 'Lun - Ven';
                    } else {
                      dayLabel = `${dayNamesShort[group.days[0]]} - ${dayNamesShort[group.days[group.days.length - 1]]}`;
                    }
                    
                    return (
                      <div 
                        key={idx}
                        className={cn(
                          "flex items-center justify-between px-3 py-2 rounded-lg",
                          isToday && "bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30"
                        )}
                      >
                        <span className={cn(
                          "font-medium",
                          isToday ? "text-[var(--color-primary)]" : "text-[#64748b]"
                        )}>
                          {dayLabel}
                          {isToday && <span className="ml-2 text-xs text-[var(--color-accent)]">(Aujourd'hui)</span>}
                        </span>
                        <span className={cn(
                          "text-sm font-medium",
                          group.schedule.closed ? "text-red-500" : "text-[#334155]"
                        )}>
                          {group.schedule.closed ? 'Fermé' : `${group.schedule.open} - ${group.schedule.close}`}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Contact info */}
            <div className="pt-4 border-t">
              <p className="text-sm text-[#64748b] mb-3">Besoin de nous contacter ?</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 rounded-full"
                  asChild
                >
                  <a href={`tel:${theme.agency?.phone}`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 rounded-full"
                  asChild
                >
                  <a href={`mailto:${theme.agency?.email}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timer Details Modal for Team Members */}
      <Dialog open={showTimerDetails} onOpenChange={setShowTimerDetails}>
        <DialogContent className="sm:max-w-md" aria-describedby="timer-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Timer className="w-5 h-5" />
              Mon activité
            </DialogTitle>
          </DialogHeader>
          <p id="timer-description" className="sr-only">Détails de votre activité et gains</p>
          
          <div className="space-y-4">
            {/* Current Timer */}
            {currentTimer ? (
              <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Chrono en cours</span>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-green-600">Actif</span>
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-mono font-bold text-emerald-700">
                    {Math.floor((currentTimer.duration_minutes || 0) / 60)}h
                    {Math.floor((currentTimer.duration_minutes || 0) % 60).toString().padStart(2, '0')}
                  </span>
                  <span className="text-lg font-semibold text-emerald-600">
                    +{safeNumber((currentTimer.duration_minutes || 0) / 60 * (currentTimer.hourly_rate || 0), 2)}€
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Taux: {safeNumber(currentTimer.hourly_rate)}€/h
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
                <Play className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">Aucun chrono actif</p>
                <Button 
                  size="sm" 
                  className="mt-2"
                  onClick={() => {
                    setShowTimerDetails(false);
                    navigate('/my-tasks');
                  }}
                >
                  Voir mes tâches
                </Button>
              </div>
            )}

            {/* Earnings Summary */}
            <div>
              <h4 className="font-medium mb-3" style={{ color: theme.colors?.primary }}>
                Mes gains
              </h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-emerald-50 text-center">
                  <p className="text-xs text-gray-500">Aujourd'hui</p>
                  <p className="font-mono font-bold text-emerald-700">
                    {safeNumber((earnings.today || 0) + realtimeEarnings, 2).toLocaleString('fr-FR')}€
                  </p>
                  {realtimeEarnings > 0 && (
                    <p className="text-xs text-emerald-500 animate-pulse">
                      +{safeNumber(realtimeEarnings, 2)}€ en cours
                    </p>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-cyan-50 text-center">
                  <p className="text-xs text-gray-500">Semaine</p>
                  <p className="font-mono font-bold text-cyan-700">
                    {safeNumber((earnings.week || 0) + realtimeEarnings, 2).toLocaleString('fr-FR')}€
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-violet-50 text-center">
                  <p className="text-xs text-gray-500">Mois</p>
                  <p className="font-mono font-bold text-violet-700">
                    {(earnings.month + realtimeEarnings).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="pt-4 border-t flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => {
                  setShowTimerDetails(false);
                  navigate('/my-invoices');
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Rapports
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Hidden audio element for notification sound */}
      <audio 
        ref={messageAudioRef} 
        src="/notification-sound.mp3" 
        preload="auto"
        style={{ display: 'none' }}
      />
    </>
  );
};

export default Header;
